import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadedFile } from 'src/interfaces/FileManager';

import DisplayHeadingWithRule from 'src/common/components/DisplayHeadingWithRule';
import {
  dashboardPageHeaderActionsSx,
  dashboardPrimaryActionButtonProps,
} from 'src/common/components/DashboardPageToolbar';
import usePersistentView from 'src/hooks/usePersistentView';
import { AppFileRouterEndpoint } from 'src/interfaces/AppFileRouter';
import { lazyPage } from 'src/routes/lazyPage';
import MediaManagerToolbar from 'src/pages/Dashboard/System/FileManager/components/MediaManagerToolbar';
import { UPLOAD_ENDPOINTS } from 'src/pages/Dashboard/System/FileManager/components/resolveUploadEndpoint';
import UploadDialog from 'src/pages/Dashboard/System/FileManager/components/UploadDialog';
import useFileManagerStore from 'src/stores/fileManagerStore';

const LazyFileManagerResolver = lazyPage(
  () => import('src/pages/Dashboard/System/FileManager/FileManagerResolver'),
);

interface MediaManagerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect?: (file: UploadedFile) => void;
  onSelectMultiple?: (files: UploadedFile[]) => void;
  multiple?: boolean;
  mode?: 'manage' | 'select';
  endpoint?: 'all' | 'image' | 'document' | 'trackMap';
  title?: string;
  showUploadButton?: boolean;
}

const MediaManagerModal = ({
  open,
  onClose,
  onSelect = () => {},
  onSelectMultiple,
  multiple = false,
  mode = 'select',
  endpoint = 'all',
  title,
  showUploadButton = true,
}: MediaManagerModalProps) => {
  const { t } = useTranslation();
  const [isUploadOpen, setUploadOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<UploadedFile[]>([]);
  const [view, setView] = usePersistentView();
  const storeEndpoint = useFileManagerStore(state => state.endpoint);

  const lockedEndpoint = endpoint !== 'all' ? endpoint : undefined;
  const allowedUploadEndpoints = useMemo(
    () => (lockedEndpoint ? [lockedEndpoint] : UPLOAD_ENDPOINTS),
    [lockedEndpoint],
  );
  const uploadInitialEndpoint = useMemo((): 'all' | AppFileRouterEndpoint => {
    if (lockedEndpoint) {
      return lockedEndpoint;
    }
    if (storeEndpoint !== 'all') {
      return storeEndpoint;
    }
    return 'image';
  }, [lockedEndpoint, storeEndpoint]);

  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
  };

  const handleFileSelect = (file: UploadedFile) => {
    onSelect(file);
    handleClose();
  };

  const handleToggleSelect = useCallback((file: UploadedFile) => {
    setSelectedFiles(prev => {
      const exists = prev.some(item => item.id === file.id);
      if (exists) {
        return prev.filter(item => item.id !== file.id);
      }
      return [...prev, file];
    });
  }, []);

  const handleAddSelected = () => {
    if (selectedFiles.length === 0 || !onSelectMultiple) return;
    onSelectMultiple(selectedFiles);
    handleClose();
  };

  const modalTitle = title || t('system.fileManager.mediaLibrary');
  const isMultiSelect = multiple && mode === 'select' && Boolean(onSelectMultiple);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        slotProps={{
          paper: {
            sx: {
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            flexShrink: 0,
            overflow: 'visible',
            pt: 2.5,
            pb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              minWidth: 0,
              flexWrap: 'nowrap',
            }}
          >
            <DisplayHeadingWithRule
              variant="h6"
              component="h2"
              sx={{ flex: '1 1 0', minWidth: 0, width: 'auto' }}
            >
              {modalTitle}
            </DisplayHeadingWithRule>
            <Box sx={dashboardPageHeaderActionsSx}>
              {showUploadButton ? (
                <Button
                  {...dashboardPrimaryActionButtonProps()}
                  startIcon={<AddIcon />}
                  onClick={() => setUploadOpen(true)}
                >
                  {t('form.upload.label')}
                </Button>
              ) : null}
              <MediaManagerToolbar view={view} onSetView={setView} compact />
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
          }}
        >
          {open ? (
            <LazyFileManagerResolver
              mode={mode}
              endpoint={endpoint}
              onSelect={isMultiSelect ? handleToggleSelect : handleFileSelect}
              multiple={isMultiSelect}
              selectedFileIds={selectedFiles.map(file => file.id)}
              showTitle={false}
              hideToolbar
              allowUpload={showUploadButton}
              view={view}
              onSetView={setView}
            />
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined" sx={{ mr: 2 }}>
            {mode === 'select' ? t('base.cancel') : t('base.close')}
          </Button>
          {isMultiSelect ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddSelected}
              disabled={selectedFiles.length === 0}
            >
              {t('system.pages.imageBlock.addSelected', { count: selectedFiles.length })}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
      <UploadDialog
        open={isUploadOpen}
        onClose={() => setUploadOpen(false)}
        initialEndpoint={uploadInitialEndpoint}
        allowedEndpoints={allowedUploadEndpoints}
      />
    </>
  );
};

export default MediaManagerModal;
